import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { AuthContext } from '../../contexts/auth';
import { supportChatApi as api } from '../../services/supportChatApi';
import { moduleForPath } from './observationProtocol';
import { LIVE_CONSENT_VERSION, publishLivePage, observePresence } from './livePageStream';
import './observation.css';

const base = '/api/v1/support-chat';
const sessionStorageKey = key => `support-usage:${key}`;
function activitySession(key) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(sessionStorageKey(key)) || 'null');
    if (saved && Date.now() - saved.at < 30 * 60000) return saved;
  } catch (_) { /* Private browsing can disable storage. */ }
  return { id: uuid(), at: Date.now() };
}

function Observation({ accountKey, module, pathname }) {
  const { t } = useTranslation('supportChat');
  const [preference, setPreference] = useState(null);
  const [session, setSession] = useState(null);
  const [tabId] = useState(() => {
    try {
      const key = `support-tab:${accountKey}`;
      const value = sessionStorage.getItem(key) || uuid(); sessionStorage.setItem(key, value); return value;
    } catch (_) { return uuid(); }
  });
  const presence = useRef(null);
  const runtime = useRef({ tabId, activity: activitySession(accountKey), queue: [], seconds: 0,
    lastInteraction: Date.now(), cursor: null, session: null, preference: null, module, activeModule: module,
    lastPath: null, lastFlush: 0, lastContact: 0, blocked: false });
  const state = runtime.current;
  state.module = module;
  state.session = session;
  state.preference = preference;
  const alive = useRef(true);

  useEffect(() => {
    const stop = observePresence({
      getPresence: () => ({ tabId: state.tabId, module: state.module, visible: !!state.module && !document.hidden, focused: document.hasFocus() }),
      onUpdate: data => {
        if (!alive.current) return;
        state.lastContact = Date.now();
        if (data.preference) setPreference(data.preference);
        setSession(state.blocked || document.hidden ? null : data.session);
      },
    });
    presence.current = stop;
    const refresh = () => stop.refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    let channel;
    const instance = uuid();
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(`support-tabs:${accountKey}`);
      channel.onmessage = ({ data }) => {
        if (data?.type === 'probe' && data.tabId === state.tabId) channel.postMessage({ type: 'claimed', instance: data.instance });
        if (data?.type === 'claimed' && data.instance === instance) {
          state.tabId = uuid();
          try { sessionStorage.setItem(`support-tab:${accountKey}`, state.tabId); } catch (_) {}
          stop.refresh();
        }
      };
      channel.postMessage({ type: 'probe', instance, tabId: state.tabId });
    }
    return () => { stop(); presence.current = null; channel?.close(); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [accountKey, state]);

  useEffect(() => { presence.current?.refresh(); }, [pathname]);

  useEffect(() => {
    if (state.activeModule !== module) {
      if (state.seconds && state.activeModule && preference?.metricsEnabled) state.queue.push({
        eventId: uuid(), sessionId: state.activity.id, module: state.activeModule, type: 'active_time', seconds: Math.min(30, state.seconds),
      });
      state.seconds = 0; state.activeModule = module;
    }
  }, [module, preference?.metricsEnabled, state]);

  useEffect(() => {
    alive.current = true;
    const activity = event => {
      if (event.isTrusted !== false) state.lastInteraction = Date.now();
      if (event.type === 'pointermove') state.cursor = { x: Math.round(event.clientX), y: Math.round(event.clientY) };
    };
    const hide = () => {
      if (!document.hidden) return;
      setSession(null);
      api.post(`${base}/presence`, { tabId: state.tabId, visible: false }).catch(() => {});
    };
    const events = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'focus'];
    events.forEach(name => window.addEventListener(name, activity, { passive: true }));
    document.addEventListener('visibilitychange', hide);
    return () => {
      alive.current = false;
      events.forEach(name => window.removeEventListener(name, activity));
      document.removeEventListener('visibilitychange', hide);
      api.post(`${base}/presence`, { tabId: state.tabId, visible: false }).catch(() => {});
    };
  }, [state]);

  // Metrics: module ID, event kind, bounded active seconds only.
  useEffect(() => {
    if (!module || !preference?.metricsEnabled || document.hidden) return;
    if (state.lastPath === pathname) return;
    state.lastPath = pathname;
    state.queue.push({ eventId: uuid(), sessionId: state.activity.id, module, type: 'page_view' });
    state.queue = state.queue.slice(-20);
  }, [pathname, module, preference?.metricsEnabled, state]);

  useEffect(() => {
    let sending = false;
    const timer = setInterval(async () => {
      const pref = state.preference;
      if (!pref?.metricsEnabled) { state.queue = []; state.seconds = 0; return; }
      if (state.module && !document.hidden && document.hasFocus() && Date.now() - state.lastInteraction < 60000) {
        if (Date.now() - state.activity.at > 30 * 60000) state.activity = { id: uuid(), at: Date.now() };
        state.activity.at = Date.now();
        try { sessionStorage.setItem(sessionStorageKey(accountKey), JSON.stringify(state.activity)); } catch (_) {}
        state.seconds += 1;
      }
      if (sending || Date.now() - state.lastFlush < 15000) return;
      if (state.seconds && state.module) {
        state.queue.push({ eventId: uuid(), sessionId: state.activity.id, module: state.module, type: 'active_time', seconds: Math.min(30, state.seconds) });
        state.seconds = 0;
      }
      if (!state.queue.length) return;
      state.queue = state.queue.slice(-20);
      const batch = [...state.queue];
      state.lastFlush = Date.now(); sending = true;
      try {
        await api.post(`${base}/usage/events`, { events: batch });
        state.queue = state.queue.filter(event => !batch.some(sent => sent.eventId === event.eventId));
      } catch (_) { /* Bounded queue and stable IDs make a retry safe. */ }
      finally { sending = false; }
    }, 1000);
    return () => clearInterval(timer);
  }, [accountKey, state]);

  const canPublish = Boolean(session && preference?.liveAllowed && preference.consentVersion === LIVE_CONSENT_VERSION && module && !state.blocked);
  useEffect(() => {
    if (!canPublish) return undefined;
    return publishLivePage({ sessionId: session._id, tabId: state.tabId,
      onStatus: status => { if (status === 'ended' && alive.current) setSession(null); } });
  }, [canPublish, session?._id, session?.tabId, state]);

  useEffect(() => {
    if (!module) {
      setSession(null); state.seconds = 0;
      api.post(`${base}/presence`, { tabId: state.tabId, visible: false }).catch(() => {});
    }
  }, [module, state]);

  const stopLive = () => {
    setSession(null);
    api.patch(`${base}/preferences`, { liveAllowed: false }).then(({ data }) => setPreference(data)).catch(() => {});
  };

  if (!canPublish) return null;
  return (
    <div className="eo-observation">
      <div className="eo-live-banner">
        <span className="eo-live-dot" />
        <span>{t('observation.liveBanner')}</span>
        <button type="button" onClick={stopLive}>{t('observation.stop')}</button>
      </div>
    </div>
  );
}

export default function SupportObservation() {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  if (!user) return null;
  const key = `${user._id || user.id}:${user.empresa_id || user.vinculo_empresa || ''}`;
  return <Observation key={key} accountKey={key} pathname={pathname} module={moduleForPath(pathname)} />;
}
